import { getTenantId } from "@shared/lib/auth";
import type * as React from "react";

import { parseApiError } from "./errors";
import { Logger } from "./logger";
import { translateUi } from "@i18n/config";

type HttpErrorEventDetail = {
  method?: string;
  url?: string;
  status?: number;
  statusText?: string;
  requestId?: string;
  correlationId?: string;
  tenantId?: string | null;
  timestamp?: string;
};

export type AdminErrorDiagnostic = {
  area: "admin";
  source: string;
  correlationId: string;
  tenantSlug: string;
  route: string;
  failingApi: string | null;
  httpStatus: number | null;
  timestamp: string;
  message: string;
};

const LAST_HTTP_WINDOW_MS = 30_000;
let lastHttpError: (HttpErrorEventDetail & { capturedAt: number }) | null =
  null;

function installHttpErrorListener() {
  if (typeof window === "undefined") return;
  const key = "__storizone_admin_error_diagnostics_listener__";
  const w = window as any;
  if (w[key]) return;
  w[key] = true;

  window.addEventListener("http:error", ((event: Event) => {
    const detail = (event as CustomEvent<HttpErrorEventDetail>).detail || {};
    lastHttpError = { ...detail, capturedAt: Date.now() };
  }) as EventListener);
}

installHttpErrorListener();

function localCorrelationId() {
  const suffix = Math.random().toString(36).slice(2, 8);
  return `admin_${Date.now().toString(36)}_${suffix}`;
}

function recentHttpError() {
  if (!lastHttpError) return null;
  return Date.now() - lastHttpError.capturedAt <= LAST_HTTP_WINDOW_MS
    ? lastHttpError
    : null;
}

function parseBody(body: unknown): any {
  if (!body || typeof body !== "string") return undefined;
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
}

function readStatus(
  error: any,
  http: HttpErrorEventDetail | null,
): number | null {
  const status =
    (typeof error?.status === "number" ? error.status : undefined) ??
    (typeof error?.statusCode === "number" ? error.statusCode : undefined) ??
    (typeof error?.response?.status === "number"
      ? error.response.status
      : undefined) ??
    (typeof error?.details?.response?.status === "number"
      ? error.details.response.status
      : undefined) ??
    (typeof http?.status === "number" ? http.status : undefined);

  return typeof status === "number" ? status : null;
}

function readCorrelationId(
  error: any,
  parsedCorrelationId: string | undefined,
  http: HttpErrorEventDetail | null,
) {
  const payload = parseBody(error?.body);
  return (
    parsedCorrelationId ||
    error?.correlationId ||
    error?.correlationID ||
    error?.requestId ||
    error?.digest ||
    payload?.correlationId ||
    payload?.requestId ||
    http?.correlationId ||
    http?.requestId ||
    localCorrelationId()
  );
}

function sanitizeApiUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost";
    const url = new URL(value, base);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split("?")[0] || null;
  }
}

function readFailingApi(
  error: any,
  http: HttpErrorEventDetail | null,
): string | null {
  return sanitizeApiUrl(
    error?.url ||
      error?.requestUrl ||
      error?.config?.url ||
      error?.response?.url ||
      error?.details?.url ||
      error?.details?.request?.url ||
      http?.url ||
      null,
  );
}

function readTenantSlug(http: HttpErrorEventDetail | null) {
  const fromAuth = getTenantId();
  if (fromAuth) return fromAuth;
  if (http?.tenantId) return http.tenantId;
  try {
    const fromStorage = window.localStorage.getItem("storizone:tenant:v1");
    if (fromStorage) return fromStorage;
  } catch {
    // ignore storage access failures
  }
  return "unknown";
}

function readRoute() {
  if (typeof window === "undefined") return "unknown";
  return window.location.pathname || "/";
}

export function buildAdminErrorDiagnostic(
  error: unknown,
  source = "error-boundary",
): AdminErrorDiagnostic {
  const err = error as any;
  const parsed = parseApiError(
    error,
    translateUi("weCouldNotLoadThisPage", "We could not load this page."),
  );
  const http = recentHttpError();
  const timestamp =
    typeof err?.timestamp === "string"
      ? err.timestamp
      : new Date().toISOString();

  return {
    area: "admin",
    source,
    correlationId: readCorrelationId(err, parsed.correlationId, http),
    tenantSlug: readTenantSlug(http),
    route: readRoute(),
    failingApi: readFailingApi(err, http),
    httpStatus: readStatus(err, http),
    timestamp,
    message: parsed.message,
  };
}

export function logAdminErrorDiagnostic(
  diagnostic: AdminErrorDiagnostic,
  error: unknown,
  info?: React.ErrorInfo | null,
) {
  const devDetails = import.meta.env.DEV
    ? {
        stack: error instanceof Error ? error.stack : undefined,
        componentStack: info?.componentStack,
      }
    : undefined;

  if (devDetails) {
    Logger.error("ADMIN_ERROR_BOUNDARY", diagnostic, devDetails);
    return;
  }

  Logger.error("ADMIN_ERROR_BOUNDARY", diagnostic);
}
